import React from "react";
import GeotabBadge from "./GeotabBadge.jsx";
import { Hint, HoverRows } from "./GeotabHover.jsx";
import { deltaTone, fmtDelta, fmtDuration, fmtTime } from "./geotabFormat.js";
import { geotabUrl } from "./geotabLinks.js";

const CELL = "border-t border-slate-100 px-2 py-1 text-[12.5px] tabular-nums";
const HEAD = "whitespace-nowrap px-2 py-1 text-left text-[10.5px] font-bold uppercase tracking-wide text-slate-500";
const SEP = "border-l border-slate-200";
const SPEED_LIMITER_KMH = 105;
// Deux vues courtes plutôt qu'un tableau de 16 colonnes : temps par rapport à l'horaire, puis conduite et respect du plan.
const VIEWS = [["time", "Temps vs prévu", 11], ["driving", "Conduite et plan", 8]];

const perDay = (value, days) => (days ? value / days : 0);
const fmtCount = (value) => value.toLocaleString("fr-CA", { maximumFractionDigits: 1 });
const fmtPct = (value) => `${value > 0 ? "+" : value < 0 ? "−" : ""}${Math.round(Math.abs(value))} %`;
const fmtDay = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("fr-CA", { weekday: "short", day: "numeric", month: "short" });

function Delta({ value, tolerance, title }) {
  return (
    <span className="inline-block w-16">
      {value == null ? <GeotabBadge tone="empty" /> : <GeotabBadge tone={deltaTone(value, tolerance)} title={title}>{fmtDelta(value)}</GeotabBadge>}
    </span>
  );
}

function Pct({ value, title }) {
  return <span className="inline-block w-16">{value == null ? <GeotabBadge tone="empty" /> : <GeotabBadge tone={deltaTone(value, 10)} title={title}>{fmtPct(value)}</GeotabBadge>}</span>;
}

function HeadHint({ rows, children }) {
  return <HoverRows title={children} rows={rows}><span className="cursor-help underline decoration-dotted underline-offset-2">{children}</span></HoverRows>;
}

// Bulles des en-têtes : calcul sur une ligne, puis un exemple chiffré.
const HINTS = {
  departure: [["Calcul", "départ du dépôt réel − prévu, médiane"], ["Effet", "toute la tournée glisse d'autant"],
    ["Heure prévue", "début de la fiche du chauffeur + inspection"], ["Exemple", "parti à 6 h 20 au lieu de 6 h → +20 min"]],
  worked: [["Calcul", "(fin − départ) réel − prévu"], ["Journées", "camions-jours propres seulement"], ["Exemple", "9 h réel, 8 h prévues → +1 h"]],
  site: [["Calcul", "sur place réel − prévu, médiane"], ["Exemple", "1 h 05 au lieu de 45 min → +20 min"]],
  sitePeers: [["Calcul", "son écart − celui des autres, même site"], ["Exemple", "lui +40, les autres +15 → +25 min"], ["Minimum", "2 visites des autres"]],
  drive: [["Calcul", "route réelle ÷ prévue"], ["Exemple", "4 h 20 pour 4 h → +8 %"]],
  drivePeers: [["Calcul", "son % comparé aux autres"], ["Exemple", "lui +30 %, autres +10 % → +18 %"]],
  off: [["Compte", "arrêts de 10 min et plus hors plan"], ["Inclus", "passages au dépôt"], ["Exclus", "changements de camion"], ["Valeur", "moyenne par jour"]],
  order: [["Valeur", "jours dans l'ordre prévu / jours"]],
  missing: [["Valeur", "chargements et livraisons sans arrêt"]],
  speed: [["Valeur", "maximum Geotab de la période"], ["Limiteur", `${SPEED_LIMITER_KMH} km/h (camions lourds, Québec)`]],
  speeding: [["Règle Geotab", "limite affichée + 12 km/h, plus de 20 s"], ["Valeur", "nombre moyen par jour"]],
  idle: [["Règle Geotab", "moteur en marche à l'arrêt, plus de 3 min 20"], ["Exclus", "chargements et livraisons"], ["Valeur", "moyenne par jour"]],
};

// Temps sur place d'une journée : somme des écarts, chaque visite dans la hovercard.
function VisitsCell({ visits, tolerance }) {
  if (!visits.length) return <span className="inline-block w-16"><GeotabBadge tone="empty" /></span>;
  const total = visits.reduce((sum, v) => sum + v.gap, 0);
  return (
    <HoverRows title="Temps sur place" rows={visits.map((v) => [v.name, `${fmtDuration(v.real)} / ${fmtDuration(v.plan)} prévues (${fmtDelta(v.gap)})${v.peer == null ? "" : ` · collègues ${fmtDelta(v.peer)}`}`])}>
      <span className="inline-block w-16 cursor-help"><GeotabBadge tone={deltaTone(total, tolerance)}>{fmtDelta(total)}</GeotabBadge></span>
    </HoverRows>
  );
}

function DayRows({ days, tolerance, period }) {
  return (
    <table className="w-full min-w-0">
      <thead>
        <tr>{["Journée", "Camion", "Travaillé", "Chargement", "Livraison", "Route", "Hors plan", "Excès", "Ralenti", "Raisons", ""].map((label, i) => <th key={i} className={HEAD}>{label}</th>)}</tr>
      </thead>
      <tbody>
        {days.map((d) => {
          const stops = d.stops.filter((s) => s.kind !== "switch");
          const off = stops.reduce((sum, s) => sum + s.dwell, 0);
          const reasons = d.reasons.filter((r) => r.kind !== "delta" || Math.abs(r.minutes) > tolerance);
          return (
            <tr key={d.date} className="align-top">
              <td className={`${CELL} whitespace-nowrap font-semibold text-slate-800`}>{fmtDay(d.date)}</td>
              <td className={`${CELL} max-w-[150px]`}>
                <span className="block truncate">{d.truck}</span>
                {d.geotab_driver && <span className="block truncate text-[11px] text-slate-500">{`session Geotab : ${d.geotab_driver}`}</span>}
              </td>
              <td className={CELL}>
                {d.clean ? <Delta value={d.worked_delta} tolerance={tolerance} title={`Prévu ${fmtTime(d.plan_departure)} → ${fmtTime(d.plan_return)}`} />
                  : <span className="inline-block w-16"><GeotabBadge tone="mute" title="Camion-jour non propre : pas compté dans « Travaillé » du chauffeur">{fmtDelta(d.worked_delta)}</GeotabBadge></span>}
              </td>
              <td className={CELL}><VisitsCell visits={d.visits.filter((v) => v.kind === "loading")} tolerance={tolerance} /></td>
              <td className={CELL}><VisitsCell visits={d.visits.filter((v) => v.kind === "delivery")} tolerance={tolerance} /></td>
              <td className={CELL}><Delta value={d.drive_real - d.drive_plan} tolerance={tolerance} title={`${fmtDuration(d.drive_real)} de route pour ${fmtDuration(d.drive_plan)} prévues`} /></td>
              <td className={CELL}><span className="inline-block w-16">{off ? <GeotabBadge tone="off" title={`${stops.length} arrêt(s)`}>{fmtDuration(off)}</GeotabBadge> : <GeotabBadge tone="empty" />}</span></td>
              <td className={CELL}>{d.speeding_count || "—"}</td>
              <td className={CELL}>{d.idle_min ? fmtDuration(d.idle_min) : "—"}</td>
              <td className={`${CELL} w-full min-w-[280px]`}>
                {reasons.length ? reasons.map((r, i) => (
                  <div key={i} className="flex items-start gap-2 text-[11.5px] leading-[17px] text-slate-700">
                    <span className="inline-block w-14 shrink-0">
                      {r.kind === "stop" ? <GeotabBadge tone="off">{fmtDuration(r.minutes)}</GeotabBadge>
                        : r.kind === "delta" ? <GeotabBadge tone={deltaTone(r.minutes, tolerance)}>{fmtDelta(r.minutes)}</GeotabBadge>
                          : <GeotabBadge tone="mute">!</GeotabBadge>}
                    </span>
                    <span className="min-w-0">{r.text}</span>
                  </div>
                )) : <span className="text-[11.5px] text-slate-500">Journée conforme à l'horaire</span>}
              </td>
              <td className={`${CELL} whitespace-nowrap`}>
                <a className="font-semibold text-accent underline underline-offset-2" href={geotabUrl({ date: d.date, period, truckId: d.truck_id })}>Dispatch vs Geotab</a>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// Écarts de chaque chauffeur réel (Geotab Drive) au prévu, sur les journées retenues de la période :
// mêmes tournées que l'Horaire chauffeur, durées aux réglages actuels (essayés en simulation), comme le reste du Calibrage.
export default function GeotabDriversSection({ drivers, tolerance, period, simulating = false, cleanDays, truckDays }) {
  const [open, setOpen] = React.useState(() => new Set());
  const [view, setView] = React.useState("time");
  const columns = VIEWS.find(([key]) => key === view)[2];
  const toggle = (name) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  return (
    <section className="min-w-0 overflow-x-auto rounded-[4px] border border-slate-200 bg-white px-2 py-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2 px-1 pb-1">
        <span className="flex items-center gap-2">
          <span className="text-[12.5px] font-bold text-slate-800">Écarts par chauffeur</span>
          <span className="inline-flex overflow-hidden rounded-[4px] border border-slate-300" role="group" aria-label="Vue">
            {VIEWS.map(([key, label]) => (
              <button key={key} type="button" aria-pressed={view === key} onClick={() => setView(key)}
                className={`h-6 px-2 text-[11.5px] font-semibold ${view === key ? "bg-accent text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>{label}</button>
            ))}
          </span>
        </span>
        <span className="text-[11px] text-slate-500">{`Chauffeur réel · prévu = mêmes tournées, réglages ${simulating ? "essayés" : "actuels"} · collègues = même site · clic sur un nom = ses journées`}</span>
      </div>
      <p className="px-1 pb-1 text-[11.5px] font-semibold text-accent">
        {`Camion-jour propre : tous les arrêts faits dans l'ordre, un seul camion, aucun arrêt hors plan à une meunerie ou au dépôt (${cleanDays} camions-jours sur ${truckDays}).`}
      </p>
      {drivers.length ? (
        <table className="w-full min-w-0">
          <thead>
            <tr>
              <th className={HEAD} rowSpan={2}>Chauffeur</th>
              <th className={HEAD} rowSpan={2}>Jours</th>
              {view === "time" ? (
                <>
                  <th className={`${HEAD} ${SEP}`} rowSpan={2}><HeadHint rows={HINTS.departure}>Départ</HeadHint></th>
                  <th className={`${HEAD} ${SEP}`} rowSpan={2}><HeadHint rows={HINTS.worked}>Travaillé · camions-jours propres</HeadHint></th>
                  <th className={`${HEAD} ${SEP}`} colSpan={2}>Chargement</th>
                  <th className={`${HEAD} ${SEP}`} colSpan={2}>Livraison</th>
                  <th className={`${HEAD} ${SEP}`} colSpan={2}>Route</th>
                </>
              ) : (
                <>
                  <th className={`${HEAD} ${SEP}`} rowSpan={2}><HeadHint rows={HINTS.off}>Hors plan / j</HeadHint></th>
                  <th className={HEAD} rowSpan={2}><HeadHint rows={HINTS.order}>Ordre</HeadHint></th>
                  <th className={HEAD} rowSpan={2}><HeadHint rows={HINTS.missing}>Non vus</HeadHint></th>
                  <th className={`${HEAD} ${SEP}`} colSpan={3}>Conduite</th>
                </>
              )}
            </tr>
            <tr>
              {view === "time" ? [0, 1, 2].map((i) => (
                <React.Fragment key={i}>
                  <th className={`${HEAD} ${SEP}`}><HeadHint rows={i === 2 ? HINTS.drive : HINTS.site}>Prévu</HeadHint></th>
                  <th className={HEAD}><HeadHint rows={i === 2 ? HINTS.drivePeers : HINTS.sitePeers}>Collègues</HeadHint></th>
                </React.Fragment>
              )) : (
                <>
                  <th className={`${HEAD} ${SEP}`}><HeadHint rows={HINTS.speed}>Vit. max</HeadHint></th>
                  <th className={HEAD}><HeadHint rows={HINTS.speeding}>Excès / j</HeadHint></th>
                  <th className={HEAD}><HeadHint rows={HINTS.idle}>Ralenti / j</HeadHint></th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {drivers.map((d) => {
              const expanded = open.has(d.driver);
              return (
                <React.Fragment key={d.driver}>
                  <tr className={expanded ? "bg-slate-50" : undefined}>
                    <td className={`${CELL} whitespace-nowrap`}>
                      <HoverRows title={d.driver} placement="right" rows={[
                        ["Camions", d.trucks.join(", ")],
                        d.other_sessions ? ["Session Geotab", `${d.other_sessions} journée(s) sous le nom d'un autre chauffeur`] : null,
                      ]}>
                        <button type="button" aria-expanded={expanded} onClick={() => toggle(d.driver)}
                          className="inline-flex items-center gap-1 border-0 bg-transparent p-0 text-[12.5px] font-semibold text-slate-800 hover:text-accent-strong">
                          <span aria-hidden="true" className="inline-block w-3 text-slate-500">{expanded ? "▾" : "▸"}</span>{d.driver}
                        </button>
                      </HoverRows>
                    </td>
                    <td className={CELL}>{d.days}</td>
                    {view === "time" ? (<>
                    <td className={`${CELL} border-l border-slate-100`}>
                      {/* Montré seulement : l'heure de début vient de la fiche du chauffeur, rien n'est proposé. */}
                      <HoverRows title={`${d.driver} · départ du dépôt`} rows={[["Journées", `${d.days}, médiane ${fmtDelta(d.departure.median)}`],
                        ["Début du service → départ", d.departure.service_median == null ? "carnet de route absent"
                          : `${fmtDuration(d.departure.service_median)}, médiane de ${d.departure.service_days} j (carnet de route)`],
                        d.departure.inspection != null && ["Inspection prévue", fmtDuration(d.departure.inspection)]]}>
                        <span className="inline-block cursor-help"><Delta value={d.departure.median} tolerance={tolerance} /></span>
                      </HoverRows>
                    </td>
                    <td className={`${CELL} border-l border-slate-100`}>
                      {d.clean_days ? <span className="inline-flex items-center gap-1.5"><Delta value={d.worked_median} tolerance={tolerance} /><span className="text-[11px] text-slate-500">{`${d.clean_days} j`}</span></span>
                        : <span className="text-[11.5px] text-slate-400">aucun camion-jour propre</span>}
                    </td>
                    {[d.loading, d.delivery].map((s, i) => (
                      <React.Fragment key={i}>
                        <td className={`${CELL} border-l border-slate-100`}><Delta value={s.gap_median} tolerance={tolerance} title={`${s.count} visite(s)`} /></td>
                        <td className={CELL}><Delta value={s.peer_median} tolerance={tolerance} title={`${s.peer_count} visite(s) comparables`} /></td>
                      </React.Fragment>
                    ))}
                    <td className={`${CELL} border-l border-slate-100`}><Pct value={d.drive.pct} title={`${fmtDuration(d.drive.real)} de route pour ${fmtDuration(d.drive.plan)} prévues`} /></td>
                    <td className={CELL}><Pct value={d.drive.peer_pct} /></td>
                    </>) : (<>
                    <td className={`${CELL} border-l border-slate-100`}>
                      <span className="inline-block w-16">{d.off_minutes ? <GeotabBadge tone="off" title={`${d.off_count} arrêt(s), ${fmtDuration(d.off_minutes)} au total`}>{fmtDuration(perDay(d.off_minutes, d.days))}</GeotabBadge> : <GeotabBadge tone="empty" />}</span>
                    </td>
                    <td className={CELL}>{`${d.order_ok}/${d.days}`}</td>
                    <td className={CELL}>{d.missing || "—"}</td>
                    <td className={`${CELL} border-l border-slate-100`}>
                      <span className="inline-block w-16">{d.max_speed ? <GeotabBadge tone={d.max_speed > SPEED_LIMITER_KMH ? "warn" : "ok"}>{`${Math.round(d.max_speed)} km/h`}</GeotabBadge> : <GeotabBadge tone="empty" />}</span>
                    </td>
                    <td className={CELL}>
                      <Hint text={`${d.speeding_count} excès, ${fmtDuration(d.speeding_minutes)} au total`}><span className="cursor-help">{fmtCount(perDay(d.speeding_count, d.days))}</span></Hint>
                    </td>
                    <td className={CELL}>{d.idle_minutes ? fmtDuration(perDay(d.idle_minutes, d.days)) : "—"}</td>
                    </>)}
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={columns} className="border-t border-slate-100 bg-slate-50 px-2 pb-2 pt-1">
                        <DayRows days={d.day_list} tolerance={tolerance} period={period} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      ) : <p className="px-1 text-[12px] text-slate-500">Aucune journée comparée sur la période.</p>}
    </section>
  );
}
